import React, { useEffect, useMemo, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { formatNumber } from "shared/utils/numberFormatter";
import {
  cohortColorsByName,
  mergeConversionNodes,
  mergeConversionLinks,
  mergeCohortNodes,
  mergeCohortLinks,
  recomputeConversionMergedPercentages,
  recomputeCohortMergedPercentages,
  rebuildAnchorsFromMergedNodes,
  SEG_CONVERTED,
  SEG_DID_NOT,
  SEG_ORDER,
} from "features/analytics/studio/utils/flowSegmentMerge";

/**
 * FlowVisualization — Sankey-style flow diagram.
 *
 * Brand primary for nodes/links in all modes; only Drop-off is grey. Conversion: two stacked segments.
 * Cohort breakdown: same stacked pattern with one color per selected cohort across the whole map.
 */

// ── Constants ────────────────────────────────────────────────────────────────

/** Flow chart accent — brand primary in every mode */
const FLOW_PRIMARY = "var(--brand-primary)";
const GREY_COLOR = "var(--text-disabled)";
/** Through cluster — teal, distinct from primary (blue/indigo) and drop-off (grey) */
const THROUGH_COLOR = "#0d9488";
const HEADER_H = 44;
const LABEL_H = 38;
const NODE_GAP = 12;
/** Narrow flow columns (rect width) */
const COL_W = 72;
const COL_GAP = 96;
const PAD_H = 48;
const PAD_V = 16;
const MIN_RECT_H = 20;
const MAX_RECT_H = 200;
const AVAILABLE_H = 400;

/** User zoom for board/editor; viewBox keeps layout coordinates stable */
const FLOW_ZOOM_MIN = 0.35;
const FLOW_ZOOM_MAX = 2.5;
const FLOW_ZOOM_STEP = 1.12;

const CONV_ORANGE = "var(--brand-primary)";
/** Others / default nodes / converted segment — same primary */
const OTHERS_COLOR = FLOW_PRIMARY;

/** Stack order within a column (top → bottom): anchor, events by count, Others, Through, Drop-off last */
function sortColumnNodes(a, b) {
  const rank = (n) => {
    if (n.isAnchor) return 0;
    if (n.isDropoff) return 4;
    if (n.isThrough) return 3;
    if (n.isOthers) return 2;
    return 1;
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  return (b.count || 0) - (a.count || 0);
}

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function anchorLetter(idx) {
  return String.fromCharCode(65 + idx);
}

/**
 * Column titles: anchor columns show "A  Event name".
 * After anchor A: A + 1 … A + stepsAfter(A). Before anchor B: B - stepsBefore(B) … B - 1 (not A + … past A's window).
 * stepsAfter / stepsBefore come from the flow API (flowsEngine) or optional flowAnchorEvents overlay.
 */
function colHeaderLabel(step, anchors) {
  if (!anchors || anchors.length === 0) {
    return { label: String(step), isAnchor: false, letter: "A" };
  }

  const byStep = [...anchors].sort((a, b) => a.step - b.step);

  const at = byStep.find((a) => a.step === step);
  if (at) {
    const letter = anchorLetter(byStep.indexOf(at));
    const shortName = at.eventName.length > 16 ? at.eventName.slice(0, 15) + "…" : at.eventName;
    return { label: `${letter}  ${shortName}`, isAnchor: true, letter };
  }

  let prev = null;
  for (let i = byStep.length - 1; i >= 0; i--) {
    if (byStep[i].step < step) {
      prev = byStep[i];
      break;
    }
  }

  let next = null;
  for (let i = 0; i < byStep.length; i++) {
    if (byStep[i].step > step) {
      next = byStep[i];
      break;
    }
  }

  if (prev && next) {
    const stepsAfterPrev =
      prev.stepsAfter !== undefined && prev.stepsAfter !== null ? prev.stepsAfter : 3;
    const stepsBeforeNext =
      next.stepsBefore !== undefined && next.stepsBefore !== null ? next.stepsBefore : 0;
    if (step > prev.step + stepsAfterPrev && step < next.step && stepsBeforeNext > 0) {
      const letter = anchorLetter(byStep.indexOf(next));
      const label = `${letter}  -  ${next.step - step}`;
      return { label, isAnchor: false, letter };
    }
  }

  if (prev) {
    const letter = anchorLetter(byStep.indexOf(prev));
    const diff = step - prev.step;
    const label = `${letter}  +  ${diff}`;
    return { label, isAnchor: false, letter };
  }

  const first = byStep[0];
  const letter = anchorLetter(0);
  const before = first.step - step;
  const label = `${letter}  -  ${before}`;
  return { label, isAnchor: false, letter };
}

/**
 * Node rects: only Drop-off is grey; primary for others; conversion stacks converted on top.
 */
function buildBandsForNode(node, rectY, rectH, conversionMode) {
  if (node.isDropoff) {
    if (conversionMode && node.segmentCounts) {
      const sc = node.segmentCounts;
      const cConv = sc[SEG_CONVERTED] || 0;
      const cDnc = sc[SEG_DID_NOT] || 0;
      const t = cConv + cDnc;
      if (t > 0 && cConv > 0 && cDnc > 0) {
        const hConv = Math.max(2, Math.round((cConv / t) * rectH));
        const hDnc = Math.max(2, rectH - hConv);
        return [
          { key: SEG_CONVERTED, y0: rectY, h: hConv, color: GREY_COLOR },
          { key: SEG_DID_NOT, y0: rectY + hConv, h: hDnc, color: GREY_COLOR },
        ];
      }
    }
    return [{ key: "_solid", y0: rectY, h: rectH, color: GREY_COLOR, solid: true }];
  }

  if (node.isOthers) {
    if (conversionMode && node.segmentCounts) {
      const sc = node.segmentCounts;
      const cConv = sc[SEG_CONVERTED] || 0;
      const cDnc = sc[SEG_DID_NOT] || 0;
      const t = cConv + cDnc;
      if (t > 0 && cConv > 0 && cDnc > 0) {
        const hConv = Math.max(2, Math.round((cConv / t) * rectH));
        const hDnc = Math.max(2, rectH - hConv);
        return [
          { key: SEG_CONVERTED, y0: rectY, h: hConv, color: FLOW_PRIMARY },
          { key: SEG_DID_NOT, y0: rectY + hConv, h: hDnc, color: CONV_ORANGE },
        ];
      }
    }
    return [{ key: "_solid", y0: rectY, h: rectH, color: OTHERS_COLOR, solid: true }];
  }

  if (node.isThrough) {
    return [{ key: "_solid", y0: rectY, h: rectH, color: THROUGH_COLOR, solid: true }];
  }

  if (!conversionMode) {
    return [{ key: "_solid", y0: rectY, h: rectH, color: FLOW_PRIMARY, solid: true }];
  }

  const sc = node.segmentCounts;
  if (!sc) {
    return [{ key: "_solid", y0: rectY, h: rectH, color: FLOW_PRIMARY, solid: true }];
  }
  const cConv = sc[SEG_CONVERTED] || 0;
  const cDnc = sc[SEG_DID_NOT] || 0;
  const t = cConv + cDnc;
  if (t <= 0) {
    return [{ key: "_solid", y0: rectY, h: rectH, color: FLOW_PRIMARY, solid: true }];
  }
  const hConv = Math.max(2, Math.round((cConv / t) * rectH));
  const hDnc = Math.max(2, rectH - hConv);
  return [
    { key: SEG_CONVERTED, y0: rectY, h: hConv, color: FLOW_PRIMARY },
    { key: SEG_DID_NOT, y0: rectY + hConv, h: hDnc, color: CONV_ORANGE },
  ];
}

/** Stacked bands for N cohorts (same geometry as conversion, dynamic colors). */
function buildBandsForCohortNode(node, rectY, rectH, segmentOrder, colorOf) {
  const sc = node.segmentCounts;
  if (!sc || !segmentOrder?.length) {
    return [{ key: "_solid", y0: rectY, h: rectH, color: FLOW_PRIMARY, solid: true }];
  }

  const weights = segmentOrder.map((s) => sc[s] || 0);
  const total = weights.reduce((a, b) => a + b, 0);
  const nonZeroSegs = segmentOrder.filter((s) => (sc[s] || 0) > 0);

  const stackGreys = (isDrop) => {
    if (total <= 0) {
      return [{ key: "_solid", y0: rectY, h: rectH, color: GREY_COLOR, solid: true }];
    }
    if (nonZeroSegs.length === 1) {
      return [{ key: nonZeroSegs[0], y0: rectY, h: rectH, color: GREY_COLOR, solid: true }];
    }
    const heights = allocateProportionalHeights(weights, rectH);
    const bands = [];
    let y = rectY;
    segmentOrder.forEach((s, i) => {
      const h = heights[i] || 0;
      if (h <= 0) return;
      bands.push({ key: s, y0: y, h, color: GREY_COLOR });
      y += h;
    });
    return bands.length ? bands : [{ key: "_solid", y0: rectY, h: rectH, color: GREY_COLOR, solid: true }];
  };

  if (node.isDropoff) return stackGreys(true);
  if (node.isThrough) return [{ key: "_solid", y0: rectY, h: rectH, color: THROUGH_COLOR, solid: true }];

  if (node.isOthers) {
    if (nonZeroSegs.length === 1) {
      const s = nonZeroSegs[0];
      return [{ key: s, y0: rectY, h: rectH, color: colorOf(s), solid: true }];
    }
    const heights = allocateProportionalHeights(weights, rectH);
    const bands = [];
    let y = rectY;
    segmentOrder.forEach((s, i) => {
      const h = heights[i] || 0;
      if (h <= 0) return;
      bands.push({ key: s, y0: y, h, color: colorOf(s) });
      y += h;
    });
    return bands.length ? bands : [{ key: "_solid", y0: rectY, h: rectH, color: OTHERS_COLOR, solid: true }];
  }

  if (nonZeroSegs.length === 1) {
    const s = nonZeroSegs[0];
    return [{ key: s, y0: rectY, h: rectH, color: colorOf(s), solid: true }];
  }
  if (total <= 0) {
    return [{ key: "_solid", y0: rectY, h: rectH, color: FLOW_PRIMARY, solid: true }];
  }
  const heights = allocateProportionalHeights(weights, rectH);
  const bands = [];
  let y = rectY;
  segmentOrder.forEach((s, i) => {
    const h = heights[i] || 0;
    if (h <= 0) return;
    bands.push({ key: s, y0: y, h, color: colorOf(s) });
    y += h;
  });
  return bands.length ? bands : [{ key: "_solid", y0: rectY, h: rectH, color: FLOW_PRIMARY, solid: true }];
}

function linkFillForSegment(seg) {
  if (seg === SEG_CONVERTED) return "color-mix(in srgb, var(--brand-primary) 22%, transparent)";
  if (seg === SEG_DID_NOT)   return "color-mix(in srgb, var(--brand-primary) 22%, transparent)";
  return "color-mix(in srgb, var(--brand-primary) 18%, transparent)";
}

/**
 * Band for a conversion segment. Single-_solid stacks (100% converted or 100% did-not) have no keyed
 * bands; map the full rect to that segment so Sankey ribbons align with node geometry.
 */
function resolveBandForSegment(item, seg) {
  const { bands, node } = item;
  const direct = bands.find((b) => b.key === seg);
  if (direct) return direct;
  const sc = node.segmentCounts;
  if (!sc) return null;
  if ((sc[seg] || 0) <= 0) return null;
  const other = seg === SEG_CONVERTED ? SEG_DID_NOT : SEG_CONVERTED;
  if ((sc[other] || 0) > 0) return null;
  if (bands.length !== 1 || !bands[0]) return null;
  const sole = bands[0];
  return { y0: sole.y0, h: sole.h, key: seg, color: sole.color, solid: sole.solid };
}

function resolveBandForCohortSegment(item, seg, segmentOrder) {
  const { bands, node } = item;
  const direct = bands.find((b) => b.key === seg);
  if (direct) return direct;
  const sc = node.segmentCounts;
  if (!sc) return null;
  if ((sc[seg] || 0) <= 0) return null;
  const othersHave = segmentOrder.filter((s) => s !== seg && (sc[s] || 0) > 0);
  if (othersHave.length > 0) return null;
  if (bands.length !== 1 || !bands[0]) return null;
  const sole = bands[0];
  return { y0: sole.y0, h: sole.h, key: seg, color: sole.color, solid: sole.solid };
}

/** Largest-remainder split: integer heights sum to totalPixels exactly (fixes Sankey overflow past bands). */
function allocateProportionalHeights(weights, totalPixels) {
  const w = weights.map((x) => Number(x) || 0);
  const sumW = w.reduce((a, b) => a + b, 0);
  const n = w.length;
  if (n === 0 || totalPixels <= 0 || sumW <= 0) return w.map(() => 0);
  const raw = w.map((wi) => (wi / sumW) * totalPixels);
  const base = raw.map((r) => Math.floor(r));
  let rem = totalPixels - base.reduce((a, b) => a + b, 0);
  const idx = raw
    .map((r, i) => ({ i, frac: r - base[i] }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...base];
  for (let k = 0; k < rem; k++) {
    out[idx[k % idx.length].i] += 1;
  }
  return w.map((wi, i) => (wi <= 0 ? 0 : out[i]));
}

function nodeRectY(nodeLayout, nodeId) {
  return nodeLayout.get(nodeId)?.rectY ?? 0;
}

// ── Layout builders ───────────────────────────────────────────────────────────

function buildDefaultLayout(nodes, links, anchors) {
  const stepMap = new Map();
  for (const node of nodes) {
    if (!stepMap.has(node.step)) stepMap.set(node.step, []);
    stepMap.get(node.step).push(node);
  }

  const sortedSteps = [...stepMap.keys()].sort((a, b) => a - b);
  const numCols = sortedSteps.length;
  const svgWidth = PAD_H * 2 + numCols * COL_W + (numCols - 1) * COL_GAP;

  const sortedColNodes = new Map();
  for (const step of sortedSteps) {
    const colNodes = stepMap.get(step) || [];
    sortedColNodes.set(step, [...colNodes].sort(sortColumnNodes));
  }

  const colX = new Map();
  for (let ci = 0; ci < sortedSteps.length; ci++) {
    colX.set(sortedSteps[ci], PAD_H + ci * (COL_W + COL_GAP));
  }

  const nodeLayout = new Map();

  for (const step of sortedSteps) {
    const colNodes = sortedColNodes.get(step);
    const x = colX.get(step);
    const totalCount = colNodes.reduce((s, n) => s + n.count, 0) || 1;
    const rectHeights = colNodes.map((n) => {
      const raw = (n.count / totalCount) * AVAILABLE_H;
      return Math.max(MIN_RECT_H, Math.min(MAX_RECT_H, Math.round(raw)));
    });

    let y = HEADER_H + PAD_V;
    for (let ni = 0; ni < colNodes.length; ni++) {
      const node = colNodes[ni];
      const rectH = rectHeights[ni];
      const color = node.isDropoff
        ? GREY_COLOR
        : node.isThrough
          ? THROUGH_COLOR
          : node.isOthers
            ? OTHERS_COLOR
            : FLOW_PRIMARY;
      const bands = buildBandsForNode(node, y + LABEL_H, rectH, false);

      nodeLayout.set(node.id, {
        x,
        labelY: y,
        rectY: y + LABEL_H,
        rectH,
        color,
        node,
        bands,
      });

      y += LABEL_H + rectH + NODE_GAP;
    }
  }

  let maxBottom = HEADER_H + PAD_V;
  for (const [, item] of nodeLayout) {
    maxBottom = Math.max(maxBottom, item.rectY + item.rectH + PAD_V);
  }
  const svgHeight = maxBottom + PAD_V;

  const srcOffsets = new Map();
  const tgtOffsets = new Map();
  for (const [id, item] of nodeLayout) {
    srcOffsets.set(id, item.rectY);
    tgtOffsets.set(id, item.rectY);
  }

  const linkLayout = [];
  const sortedLinks = [...links].sort((a, b) => {
    const sA = nodeLayout.get(a.source);
    const sB = nodeLayout.get(b.source);
    return (sA?.rectY || 0) - (sB?.rectY || 0);
  });

  for (const link of sortedLinks) {
    const src = nodeLayout.get(link.source);
    const tgt = nodeLayout.get(link.target);
    if (!src || !tgt) continue;
    const srcCount = src.node.count || 1;
    const tgtCount = tgt.node.count || 1;
    const srcH = Math.max(2, Math.round((link.value / srcCount) * src.rectH));
    const tgtH = Math.max(2, Math.round((link.value / tgtCount) * tgt.rectH));
    const srcY1 = srcOffsets.get(link.source);
    const tgtY1 = tgtOffsets.get(link.target);
    srcOffsets.set(link.source, srcY1 + srcH);
    tgtOffsets.set(link.target, tgtY1 + tgtH);
    linkLayout.push({
      link,
      x1: src.x + COL_W,
      y1: srcY1,
      h1: srcH,
      x2: tgt.x,
      y2: tgtY1,
      h2: tgtH,
      value: link.value,
      fill: "color-mix(in srgb, var(--brand-primary) 18%, transparent)",
      segment: null,
    });
  }

  const headers = sortedSteps.map((step) => ({
    step,
    x: colX.get(step),
    ...colHeaderLabel(step, anchors),
  }));

  return { nodeLayout, linkLayout, svgWidth, svgHeight, headers, conversionMode: false };
}

function buildConversionLayout(nodes, links, anchors) {
  const stepMap = new Map();
  for (const node of nodes) {
    if (!stepMap.has(node.step)) stepMap.set(node.step, []);
    stepMap.get(node.step).push(node);
  }

  const sortedSteps = [...stepMap.keys()].sort((a, b) => a - b);
  const numCols = sortedSteps.length;
  const svgWidth = PAD_H * 2 + numCols * COL_W + (numCols - 1) * COL_GAP;

  const sortedColNodes = new Map();
  for (const step of sortedSteps) {
    const colNodes = stepMap.get(step) || [];
    sortedColNodes.set(step, [...colNodes].sort(sortColumnNodes));
  }

  const colX = new Map();
  for (let ci = 0; ci < sortedSteps.length; ci++) {
    colX.set(sortedSteps[ci], PAD_H + ci * (COL_W + COL_GAP));
  }

  const nodeLayout = new Map();

  for (const step of sortedSteps) {
    const colNodes = sortedColNodes.get(step);
    const x = colX.get(step);
    const totalCount = colNodes.reduce((s, n) => s + n.count, 0) || 1;
    const rectHeights = colNodes.map((n) => {
      const raw = (n.count / totalCount) * AVAILABLE_H;
      return Math.max(MIN_RECT_H, Math.min(MAX_RECT_H, Math.round(raw)));
    });

    let y = HEADER_H + PAD_V;
    for (let ni = 0; ni < colNodes.length; ni++) {
      const node = colNodes[ni];
      const rectH = rectHeights[ni];
      const rectY = y + LABEL_H;
      const bands = buildBandsForNode(node, rectY, rectH, true);

      nodeLayout.set(node.id, {
        x,
        labelY: y,
        rectY,
        rectH,
        color: FLOW_PRIMARY,
        node,
        bands,
      });

      y += LABEL_H + rectH + NODE_GAP;
    }
  }

  let maxBottom = HEADER_H + PAD_V;
  for (const [, item] of nodeLayout) {
    maxBottom = Math.max(maxBottom, item.rectY + item.rectH + PAD_V);
  }
  const svgHeight = maxBottom + PAD_V;

  const linkLayout = [];

  const sortSegLinks = (a, b) => {
    const dy = nodeRectY(nodeLayout, a.source) - nodeRectY(nodeLayout, b.source);
    if (dy !== 0) return dy;
    const ty = nodeRectY(nodeLayout, a.target) - nodeRectY(nodeLayout, b.target);
    if (ty !== 0) return ty;
    return String(a.source).localeCompare(String(b.source));
  };

  for (const seg of SEG_ORDER) {
    const segLinks = links.filter((l) => (l.segmentValues?.[seg] || 0) > 0).sort(sortSegLinks);

    const linkKey = (l) => `${l.source}::${l.target}`;
    const srcY1 = new Map();
    const srcH = new Map();
    const tgtY1 = new Map();
    const tgtH = new Map();

    const bySource = new Map();
    for (const link of segLinks) {
      if (!bySource.has(link.source)) bySource.set(link.source, []);
      bySource.get(link.source).push(link);
    }
    for (const [, grp] of bySource) {
      grp.sort((a, b) => nodeRectY(nodeLayout, a.target) - nodeRectY(nodeLayout, b.target));
      const srcItem = nodeLayout.get(grp[0].source);
      const srcBand = resolveBandForSegment(srcItem, seg);
      const srcSegTotal = srcItem?.node.segmentCounts?.[seg] || 0;
      if (!srcBand || srcSegTotal <= 0) continue;
      const weights = grp.map((l) => l.segmentValues[seg]);
      const sumLv = weights.reduce((a, b) => a + b, 0);
      if (sumLv <= 0) continue;
      const totalPx = Math.max(
        0,
        Math.min(srcBand.h, Math.round((sumLv / srcSegTotal) * srcBand.h))
      );
      const heights = allocateProportionalHeights(weights, totalPx);
      let y = srcBand.y0;
      grp.forEach((link, i) => {
        const h = heights[i] || 0;
        const k = linkKey(link);
        srcY1.set(k, y);
        srcH.set(k, h);
        y += h;
      });
    }

    const byTarget = new Map();
    for (const link of segLinks) {
      if (!byTarget.has(link.target)) byTarget.set(link.target, []);
      byTarget.get(link.target).push(link);
    }
    for (const [, grp] of byTarget) {
      grp.sort((a, b) => nodeRectY(nodeLayout, a.source) - nodeRectY(nodeLayout, b.source));
      const tgtItem = nodeLayout.get(grp[0].target);
      const tgtBand = resolveBandForSegment(tgtItem, seg);
      const tgtSegTotal = tgtItem?.node.segmentCounts?.[seg] || 0;
      if (!tgtBand || tgtSegTotal <= 0) continue;
      const weights = grp.map((l) => l.segmentValues[seg]);
      const sumLv = weights.reduce((a, b) => a + b, 0);
      if (sumLv <= 0) continue;
      const totalPx = Math.max(
        0,
        Math.min(tgtBand.h, Math.round((sumLv / tgtSegTotal) * tgtBand.h))
      );
      const heights = allocateProportionalHeights(weights, totalPx);
      let y = tgtBand.y0;
      grp.forEach((link, i) => {
        const h = heights[i] || 0;
        const k = linkKey(link);
        tgtY1.set(k, y);
        tgtH.set(k, h);
        y += h;
      });
    }

    for (const link of segLinks) {
      const lv = link.segmentValues[seg];
      if (lv <= 0) continue;
      const k = linkKey(link);
      const y1s = srcY1.get(k);
      const h1 = srcH.get(k);
      const y2t = tgtY1.get(k);
      const h2 = tgtH.get(k);
      if (y1s == null || h1 == null || y2t == null || h2 == null) continue;
      if (h1 <= 0 || h2 <= 0) continue;

      const srcItem = nodeLayout.get(link.source);
      const tgtItem = nodeLayout.get(link.target);
      if (!srcItem || !tgtItem) continue;

      linkLayout.push({
        link,
        x1: srcItem.x + COL_W,
        y1: y1s,
        h1,
        x2: tgtItem.x,
        y2: y2t,
        h2,
        value: lv,
        fill: linkFillForSegment(seg),
        segment: seg,
      });
    }
  }

  const headers = sortedSteps.map((step) => ({
    step,
    x: colX.get(step),
    ...colHeaderLabel(step, anchors),
  }));

  return { nodeLayout, linkLayout, svgWidth, svgHeight, headers, conversionMode: true };
}

function buildCohortLayout(nodes, links, anchors, segmentOrder, segmentColors) {
  const colorOf = (s) => segmentColors[s] || FLOW_PRIMARY;

  const stepMap = new Map();
  for (const node of nodes) {
    if (!stepMap.has(node.step)) stepMap.set(node.step, []);
    stepMap.get(node.step).push(node);
  }

  const sortedSteps = [...stepMap.keys()].sort((a, b) => a - b);
  const numCols = sortedSteps.length;
  const svgWidth = PAD_H * 2 + numCols * COL_W + (numCols - 1) * COL_GAP;

  const sortedColNodes = new Map();
  for (const step of sortedSteps) {
    const colNodes = stepMap.get(step) || [];
    sortedColNodes.set(step, [...colNodes].sort(sortColumnNodes));
  }

  const colX = new Map();
  for (let ci = 0; ci < sortedSteps.length; ci++) {
    colX.set(sortedSteps[ci], PAD_H + ci * (COL_W + COL_GAP));
  }

  const nodeLayout = new Map();

  for (const step of sortedSteps) {
    const colNodes = sortedColNodes.get(step);
    const x = colX.get(step);
    const totalCount = colNodes.reduce((s, n) => s + n.count, 0) || 1;
    const rectHeights = colNodes.map((n) => {
      const raw = (n.count / totalCount) * AVAILABLE_H;
      return Math.max(MIN_RECT_H, Math.min(MAX_RECT_H, Math.round(raw)));
    });

    let y = HEADER_H + PAD_V;
    for (let ni = 0; ni < colNodes.length; ni++) {
      const node = colNodes[ni];
      const rectH = rectHeights[ni];
      const rectY = y + LABEL_H;
      const bands = buildBandsForCohortNode(node, rectY, rectH, segmentOrder, colorOf);

      nodeLayout.set(node.id, {
        x,
        labelY: y,
        rectY,
        rectH,
        color: FLOW_PRIMARY,
        node,
        bands,
      });

      y += LABEL_H + rectH + NODE_GAP;
    }
  }

  let maxBottom = HEADER_H + PAD_V;
  for (const [, item] of nodeLayout) {
    maxBottom = Math.max(maxBottom, item.rectY + item.rectH + PAD_V);
  }
  const svgHeight = maxBottom + PAD_V;

  const linkLayout = [];

  const sortSegLinks = (a, b) => {
    const dy = nodeRectY(nodeLayout, a.source) - nodeRectY(nodeLayout, b.source);
    if (dy !== 0) return dy;
    const ty = nodeRectY(nodeLayout, a.target) - nodeRectY(nodeLayout, b.target);
    if (ty !== 0) return ty;
    return String(a.source).localeCompare(String(b.source));
  };

  for (const seg of segmentOrder) {
    const segLinks = links.filter((l) => (l.segmentValues?.[seg] || 0) > 0).sort(sortSegLinks);

    const linkKey = (l) => `${l.source}::${l.target}`;
    const srcY1 = new Map();
    const srcH = new Map();
    const tgtY1 = new Map();
    const tgtH = new Map();

    const bySource = new Map();
    for (const link of segLinks) {
      if (!bySource.has(link.source)) bySource.set(link.source, []);
      bySource.get(link.source).push(link);
    }
    for (const [, grp] of bySource) {
      grp.sort((a, b) => nodeRectY(nodeLayout, a.target) - nodeRectY(nodeLayout, b.target));
      const srcItem = nodeLayout.get(grp[0].source);
      const srcBand = resolveBandForCohortSegment(srcItem, seg, segmentOrder);
      const srcSegTotal = srcItem?.node.segmentCounts?.[seg] || 0;
      if (!srcBand || srcSegTotal <= 0) continue;
      const weights = grp.map((l) => l.segmentValues[seg]);
      const sumLv = weights.reduce((a, b) => a + b, 0);
      if (sumLv <= 0) continue;
      const totalPx = Math.max(
        0,
        Math.min(srcBand.h, Math.round((sumLv / srcSegTotal) * srcBand.h))
      );
      const heights = allocateProportionalHeights(weights, totalPx);
      let y = srcBand.y0;
      grp.forEach((link, i) => {
        const h = heights[i] || 0;
        const k = linkKey(link);
        srcY1.set(k, y);
        srcH.set(k, h);
        y += h;
      });
    }

    const byTarget = new Map();
    for (const link of segLinks) {
      if (!byTarget.has(link.target)) byTarget.set(link.target, []);
      byTarget.get(link.target).push(link);
    }
    for (const [, grp] of byTarget) {
      grp.sort((a, b) => nodeRectY(nodeLayout, a.source) - nodeRectY(nodeLayout, b.source));
      const tgtItem = nodeLayout.get(grp[0].target);
      const tgtBand = resolveBandForCohortSegment(tgtItem, seg, segmentOrder);
      const tgtSegTotal = tgtItem?.node.segmentCounts?.[seg] || 0;
      if (!tgtBand || tgtSegTotal <= 0) continue;
      const weights = grp.map((l) => l.segmentValues[seg]);
      const sumLv = weights.reduce((a, b) => a + b, 0);
      if (sumLv <= 0) continue;
      const totalPx = Math.max(
        0,
        Math.min(tgtBand.h, Math.round((sumLv / tgtSegTotal) * tgtBand.h))
      );
      const heights = allocateProportionalHeights(weights, totalPx);
      let y = tgtBand.y0;
      grp.forEach((link, i) => {
        const h = heights[i] || 0;
        const k = linkKey(link);
        tgtY1.set(k, y);
        tgtH.set(k, h);
        y += h;
      });
    }

    for (const link of segLinks) {
      const lv = link.segmentValues[seg];
      if (lv <= 0) continue;
      const k = linkKey(link);
      const y1s = srcY1.get(k);
      const h1 = srcH.get(k);
      const y2t = tgtY1.get(k);
      const h2 = tgtH.get(k);
      if (y1s == null || h1 == null || y2t == null || h2 == null) continue;
      if (h1 <= 0 || h2 <= 0) continue;

      const srcItem = nodeLayout.get(link.source);
      const tgtItem = nodeLayout.get(link.target);
      if (!srcItem || !tgtItem) continue;

      linkLayout.push({
        link,
        x1: srcItem.x + COL_W,
        y1: y1s,
        h1,
        x2: tgtItem.x,
        y2: y2t,
        h2,
        value: lv,
        fill: hexToRgba(colorOf(seg), 0.22),
        segment: seg,
      });
    }
  }

  const headers = sortedSteps.map((step) => ({
    step,
    x: colX.get(step),
    ...colHeaderLabel(step, anchors),
  }));

  return { nodeLayout, linkLayout, svgWidth, svgHeight, headers, conversionMode: true };
}

function linkTooltipLabels(nodeLayout, link) {
  if (!link) return { sourceLabel: "", targetLabel: "" };
  const src = nodeLayout.get(link.source)?.node;
  const tgt = nodeLayout.get(link.target)?.node;
  const trunc = (s) => (String(s).length > 44 ? `${String(s).slice(0, 43)}…` : String(s));
  return {
    sourceLabel: src?.eventName ? trunc(src.eventName) : "",
    targetLabel: tgt?.eventName ? trunc(tgt.eventName) : "",
  };
}

function linkTooltipFromTotal(nodeLayout, link, segment, stackedSegmentMode) {
  const srcItem = nodeLayout.get(link?.source);
  if (!srcItem?.node) return 0;
  if (stackedSegmentMode && segment && srcItem.node.segmentCounts) {
    return Number(srcItem.node.segmentCounts[segment] ?? 0);
  }
  return Number(srcItem.node.count ?? 0);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FlowVisualization({ data, height, flowAnchorEvents, contentScale = 1 }) {
  const svgRef = useRef(null);
  const [linkTooltip, setLinkTooltip] = useState(null);
  const [zoom, setZoom] = useState(1);
  const fz = typeof contentScale === "number" && Number.isFinite(contentScale) ? contentScale : 1;

  const clampZoom = (z) => Math.min(FLOW_ZOOM_MAX, Math.max(FLOW_ZOOM_MIN, z));

  useEffect(() => {
    setZoom(1);
  }, [data]);

  const prepared = useMemo(() => {
    const raw = data || {};
    let anchors = raw.anchors || [];
    if (flowAnchorEvents?.length && anchors.length) {
      anchors = anchors.map((a, i) => {
        const fe = flowAnchorEvents[i];
        if (!fe) return a;
        return {
          ...a,
          stepsBefore:
            fe.stepsBefore !== undefined && fe.stepsBefore !== null ? fe.stepsBefore : a.stepsBefore,
          stepsAfter:
            fe.stepsAfter !== undefined && fe.stepsAfter !== null ? fe.stepsAfter : a.stepsAfter,
        };
      });
    }
    const bd = raw.breakdown;

    if (bd?.mode === "cohorts" && bd?.values?.length > 0 && bd?.data) {
      const order = bd.values;
      const segmentColors = cohortColorsByName(order);
      const mergedNodes = mergeCohortNodes(order, bd.data);
      anchors = rebuildAnchorsFromMergedNodes(mergedNodes, anchors);
      const nodes = recomputeCohortMergedPercentages(mergedNodes, anchors);
      const links = mergeCohortLinks(order, bd.data);
      if (!nodes.length) {
        return {
          nodes: [],
          links: [],
          anchors,
          conversionLegend: false,
          cohortLegend: null,
          layoutInput: null,
          segmentOrder: order,
          segmentColors,
        };
      }
      return {
        nodes,
        links,
        anchors,
        conversionLegend: false,
        cohortLegend: { order, colors: segmentColors },
        layoutInput: "cohorts",
        segmentOrder: order,
        segmentColors,
      };
    }

    if (bd?.mode === "conversion" && bd?.data?.[SEG_CONVERTED] && bd?.data?.[SEG_DID_NOT]) {
      const conv = bd.data[SEG_CONVERTED];
      const dnc = bd.data[SEG_DID_NOT];
      const mergedNodes = mergeConversionNodes(conv.nodes || [], dnc.nodes || []);
      const nodes = recomputeConversionMergedPercentages(mergedNodes, anchors);
      const links = mergeConversionLinks(conv.links || [], dnc.links || []);
      if (!nodes.length) {
        return {
          nodes: [],
          links: [],
          anchors,
          conversionLegend: true,
          cohortLegend: null,
          layoutInput: null,
          segmentOrder: null,
          segmentColors: null,
        };
      }
      return {
        nodes,
        links,
        anchors,
        conversionLegend: true,
        cohortLegend: null,
        layoutInput: "conversion",
        segmentOrder: null,
        segmentColors: null,
      };
    }

    return {
      nodes: raw.nodes || [],
      links: raw.links || [],
      anchors,
      conversionLegend: false,
      cohortLegend: null,
      layoutInput: "default",
      segmentOrder: null,
      segmentColors: null,
    };
  }, [data, flowAnchorEvents]);

  const { nodes, links, anchors, conversionLegend, cohortLegend, layoutInput, segmentOrder, segmentColors } = prepared;

  const layout = useMemo(() => {
    if (!nodes.length) return null;
    if (layoutInput === "conversion") {
      return buildConversionLayout(nodes, links, anchors);
    }
    if (layoutInput === "cohorts" && segmentOrder?.length && segmentColors) {
      return buildCohortLayout(nodes, links, anchors, segmentOrder, segmentColors);
    }
    return buildDefaultLayout(nodes, links, anchors);
  }, [nodes, links, anchors, layoutInput, segmentOrder, segmentColors]);

  const stackedSegmentMode = !!(layout?.conversionMode && (conversionLegend || cohortLegend));

  if (!data || !prepared.nodes.length) {
    return (
      <div
        className="flex items-center justify-center text-sm"
        style={{ height: height || 400, color: "var(--text-muted)" }}
      >
        No flow data. Select an event and run the query.
      </div>
    );
  }

  if (!layout) return null;

  const { nodeLayout, linkLayout, svgWidth, svgHeight, headers, conversionMode } = layout;

  const renderLink = (item, idx) => {
    const { x1, y1, h1, x2, y2, h2, value, fill, link, segment } = item;
    const cx = (x1 + x2) / 2;
    const pathTop = `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
    const d = `${pathTop} L ${x2} ${y2 + h2} C ${cx} ${y2 + h2}, ${cx} ${y1 + h1}, ${x1} ${y1 + h1} Z`;
    const showTooltip = (e) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { sourceLabel, targetLabel } = linkTooltipLabels(nodeLayout, link);
      const fromTotal = linkTooltipFromTotal(nodeLayout, link, segment, stackedSegmentMode);
      const v = Number(value) || 0;
      const pct = fromTotal > 0 ? (v / fromTotal) * 100 : null;
      setLinkTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        value: v,
        segment,
        sourceLabel,
        targetLabel,
        fromTotal,
        pct,
      });
    };

    return (
      <path
        key={`${idx}-${segment ?? "all"}-${link?.source ?? ""}-${link?.target ?? ""}`}
        d={d}
        fill={fill || "color-mix(in srgb, var(--brand-primary) 18%, transparent)"}
        stroke="none"
        className="transition-opacity hover:opacity-75 cursor-pointer"
        onMouseEnter={showTooltip}
        onMouseMove={showTooltip}
        onMouseLeave={() => setLinkTooltip(null)}
      />
    );
  };

  const renderNode = ([id, item]) => {
    const { x, labelY, rectY, rectH, node, bands } = item;
    const name = node.eventName.length > 18 ? node.eventName.slice(0, 17) + "…" : node.eventName;
    const pct = node.percentage != null ? `${node.percentage}%` : "";
    const countStr = formatNumber(node.count);

    const titleY = labelY + 10;
    const textX = x + 4;

    return (
      <g key={id}>
        <text
          x={textX}
          y={titleY}
          dominantBaseline="middle"
          fontSize={Math.max(8, Math.round(11 * fz))}
          fontWeight={700}
          fill="var(--text-primary)"
          fontFamily="Inter, sans-serif"
        >
          {name}
        </text>

        <text
          x={textX}
          y={labelY + 26}
          dominantBaseline="middle"
          fontSize={Math.max(7, Math.round(10 * fz))}
          fill="var(--text-secondary)"
          fontFamily="Inter, sans-serif"
        >
          {pct}
          {"  "}
          <tspan fill="var(--text-muted)">{countStr}</tspan>
        </text>

        {bands.map((band, bi) => (
          <rect
            key={`${id}-b-${bi}`}
            x={x}
            y={band.y0}
            width={COL_W}
            height={band.h}
            rx={3}
            ry={3}
            fill={band.color}
            opacity={
              node.isAnchor && !stackedSegmentMode
                ? 1
                : band.solid && node.isDropoff
                  ? 0.95
                  : 0.9
            }
            stroke={stackedSegmentMode && !band.solid ? "var(--surface-base)" : "none"}
            strokeOpacity={stackedSegmentMode && !band.solid ? 0.4 : 1}
            strokeWidth={1}
          />
        ))}
      </g>
    );
  };

  const renderHeader = (hdr) => {
    const cx = hdr.x + COL_W / 2;
    const textY = HEADER_H / 2;

    return (
      <g key={`hdr-${hdr.step}`}>
        <text
          x={cx}
          y={textY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={hdr.isAnchor ? Math.max(8, Math.round(11 * fz)) : Math.max(7, Math.round(10 * fz))}
          fontWeight={hdr.isAnchor ? 700 : 500}
          fill={hdr.isAnchor ? "var(--text-primary)" : "var(--text-muted)"}
          fontFamily="Inter, sans-serif"
        >
          {hdr.label}
        </text>
      </g>
    );
  };

  const svgH = height || svgHeight;
  const vbW = Math.max(svgWidth, 600);
  const vbH = svgHeight;

  return (
    <div
      className="relative w-full overflow-auto rounded-md"
      style={{
        minHeight: svgH,
        maxHeight: height != null ? height : undefined,
      }}
    >
      <div
        className="absolute top-2 right-2 z-30 flex items-center gap-0.5 rounded-lg border bg-surface/95 shadow-sm py-0.5 pl-1 pr-0.5"
        style={{ borderColor: "var(--border-default)", fontFamily: "Inter, sans-serif" }}
      >
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:opacity-40 disabled:pointer-events-none"
          aria-label="Zoom out"
          title="Zoom out"
          disabled={zoom <= FLOW_ZOOM_MIN + 1e-6}
          onClick={() => setZoom((z) => clampZoom(z / FLOW_ZOOM_STEP))}
        >
          <ZoomOut className="h-4 w-4" strokeWidth={2} />
        </button>
        <span className="min-w-[2.75rem] text-center text-[11px] font-medium tabular-nums text-ink-secondary px-0.5">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:opacity-40 disabled:pointer-events-none"
          aria-label="Zoom in"
          title="Zoom in"
          disabled={zoom >= FLOW_ZOOM_MAX - 1e-6}
          onClick={() => setZoom((z) => clampZoom(z * FLOW_ZOOM_STEP))}
        >
          <ZoomIn className="h-4 w-4" strokeWidth={2} />
        </button>
        <button
          type="button"
          className="hidden sm:inline-flex h-8 items-center justify-center rounded-md px-2 text-[11px] font-medium text-ink-muted hover:bg-surface-hover hover:text-ink"
          aria-label="Reset zoom"
          title="Reset zoom to 100%"
          onClick={() => setZoom(1)}
        >
          Reset
        </button>
      </div>
      <svg
        ref={svgRef}
        width={vbW * zoom}
        height={vbH * zoom}
        viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMinYMin meet"
        style={{ display: "block" }}
      >
        {stackedSegmentMode ? (
          <>
            {[...nodeLayout.entries()].map(renderNode)}
            {linkLayout.map(renderLink)}
          </>
        ) : (
          <>
            {linkLayout.map(renderLink)}
            {[...nodeLayout.entries()].map(renderNode)}
          </>
        )}
        {headers.map(renderHeader)}
      </svg>

      {conversionLegend && stackedSegmentMode && (
        <div
          className="flex flex-wrap items-center gap-6 px-4 py-3 text-xs border-t"
          style={{ borderColor: "var(--border-default)", fontFamily: "Inter, sans-serif" }}
        >
          <span className="flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: FLOW_PRIMARY }} />
            Converted
          </span>
          <span className="flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: CONV_ORANGE }} />
            Did not convert
          </span>
        </div>
      )}

      {cohortLegend && stackedSegmentMode && (
        <div
          className="flex flex-wrap items-center gap-6 px-4 py-3 text-xs border-t"
          style={{ borderColor: "var(--border-default)", fontFamily: "Inter, sans-serif" }}
        >
          {cohortLegend.order.map((name) => (
            <span key={name} className="flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: cohortLegend.colors[name] || FLOW_PRIMARY }}
              />
              {name}
            </span>
          ))}
        </div>
      )}

      {linkTooltip && (
        <div
          className="pointer-events-none absolute z-50 rounded-lg text-xs"
          style={{
            left: linkTooltip.x + 12,
            top: linkTooltip.y - 8,
            minWidth: 200,
            maxWidth: 320,
            padding: "10px 12px",
            backgroundColor: "var(--surface-base)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {linkTooltip.sourceLabel ? (
            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)", lineHeight: 1.3 }}>
              {linkTooltip.sourceLabel}
            </div>
          ) : null}
          {linkTooltip.targetLabel ? (
            <div
              style={{
                fontWeight: 600,
                fontSize: 12,
                color: "var(--text-secondary)",
                marginTop: linkTooltip.sourceLabel ? 4 : 0,
                lineHeight: 1.35,
              }}
            >
              {linkTooltip.targetLabel}
            </div>
          ) : null}
          {linkTooltip.segment ? (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>{linkTooltip.segment}</div>
          ) : null}
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              marginTop: linkTooltip.segment ? 8 : 10,
              paddingTop: 8,
              borderTop: `1px solid ${"var(--border-default)"}`,
              fontSize: 12,
              color: "var(--text-secondary)",
            }}
          >
            <span style={{ lineHeight: 1.4 }}>
              <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>{formatNumber(linkTooltip.value)}</span>
              <span style={{ color: "var(--text-muted)", fontWeight: 500 }}> of </span>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{formatNumber(linkTooltip.fromTotal)}</span>
            </span>
            {linkTooltip.pct != null ? (
              <span style={{ fontWeight: 700, color: "var(--brand-primary)", flexShrink: 0, fontSize: 13 }}>
                {linkTooltip.pct.toFixed(1)}%
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
